// Toast. Top-of-screen feedback for short-lived success/error/warning/info.
// Self-contained: the parent owns open/close via onDismiss. The component
// does NOT auto-dismiss itself (auto-close timing is a product concern
// that should live in the parent). See DESIGN.md §9.13.

import type { CSSProperties, ReactNode } from "react";
import { useEffect } from "react";

export type ToastKind = "success" | "error" | "warning" | "info";

type Props = {
  kind: ToastKind;
  message: string;
  icon?: ReactNode;
  onDismiss?: () => void;
  // Auto-dismiss in milliseconds. 0 or undefined disables auto-dismiss.
  autoCloseMs?: number;
};

const kindColor: Record<ToastKind, string> = {
  success: "var(--color-cash)",
  error: "var(--color-danger)",
  warning: "var(--color-card)",
  info: "var(--color-transfer)",
};

const roleFor: Record<ToastKind, "alert" | "status"> = {
  success: "alert",
  error: "alert",
  warning: "alert",
  info: "status",
};

const baseStyle: CSSProperties = {
  position: "fixed",
  top: "var(--space-4)",
  left: "var(--space-4)",
  right: "var(--space-4)",
  zIndex: 300,
  display: "flex",
  alignItems: "center",
  gap: "var(--space-2)",
  padding: "var(--space-3) var(--space-4)",
  borderRadius: "var(--radius-md)",
  boxShadow: "var(--shadow-md)",
  color: "var(--text-inverse)",
  fontSize: "var(--font-size-14)",
  fontWeight: 500,
  animation: "vl-toast-in 200ms cubic-bezier(0.2, 0, 0, 1)",
};

export function Toast({
  kind,
  message,
  icon,
  onDismiss,
  autoCloseMs,
}: Props) {
  useEffect(() => {
    if (!autoCloseMs || autoCloseMs <= 0 || !onDismiss) return;
    const t = setTimeout(onDismiss, autoCloseMs);
    return () => clearTimeout(t);
  }, [autoCloseMs, onDismiss]);

  const style: CSSProperties = {
    ...baseStyle,
    background: kindColor[kind],
  };

  return (
    <div role={roleFor[kind]} style={style}>
      {icon ? (
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
          }}
        >
          {icon}
        </span>
      ) : null}
      <span style={{ flex: 1 }}>{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--text-inverse)",
            cursor: "pointer",
            fontSize: 16,
            lineHeight: 1,
            padding: 0,
            marginLeft: "var(--space-1)",
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}